import { jsx } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useScrollFade, useIsoLayoutEffect, revealInScroller } from "./scroller.js";
import { TabsTab } from "../vendor/@base-ui/react/tabs/tab/TabsTab.js";
import { TabsList } from "../vendor/@base-ui/react/tabs/list/TabsList.js";
import { TabsPanel } from "../vendor/@base-ui/react/tabs/panel/TabsPanel.js";
import { TabsRoot } from "../vendor/@base-ui/react/tabs/root/TabsRoot.js";
const TabsContext = React.createContext({ level: "pill", selected: void 0 });
function TabsInner(props, forwardedRef) {
  const { level = "pill", value, defaultValue, onValueChange, className, children, ...rest } = props;
  const isControlled = value !== void 0;
  const [mirror, setMirror] = React.useState(defaultValue);
  const selected = isControlled ? value : mirror;
  const handleValueChange = (next, details) => {
    const nextValue = next;
    if (!isControlled) setMirror(nextValue);
    if (details.reason !== "none" || nextValue == null) return;
    onValueChange?.(nextValue);
  };
  const context = React.useMemo(() => ({ level, selected }), [level, selected]);
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: context, children: /* @__PURE__ */ jsx(
    TabsRoot,
    {
      ...rest,
      ref: forwardedRef,
      className: cx("ev-tabs", className),
      "data-level": level,
      value,
      defaultValue,
      onValueChange: handleValueChange,
      children
    }
  ) });
}
const TabsBase = React.forwardRef(TabsInner);
TabsBase.displayName = "Tabs";
const Tabs = TabsBase;
const TabList = React.forwardRef(function TabList2(props, forwardedRef) {
  const { className, children, activateOnFocus = false, loopFocus = true, ...rest } = props;
  const { level, selected } = React.useContext(TabsContext);
  const listRef = React.useRef(null);
  React.useImperativeHandle(forwardedRef, () => listRef.current, []);
  useScrollFade(listRef);
  const mountedRef = React.useRef(false);
  useIsoLayoutEffect(() => {
    const list = listRef.current;
    if (list) revealInScroller(list, list.querySelector('[role="tab"][aria-selected="true"]'), mountedRef.current);
    mountedRef.current = true;
  }, [selected]);
  return /* @__PURE__ */ jsx(
    TabsList,
    {
      ...rest,
      ref: listRef,
      activateOnFocus,
      loopFocus,
      className: cx(level === "line" ? "ev-tabs-line" : "ev-segmented", "ev-scroll-fade", className),
      "data-level": level,
      children
    }
  );
});
const Tab = React.forwardRef(function Tab2(props, forwardedRef) {
  return /* @__PURE__ */ jsx(TabsTab, { ...props, ref: forwardedRef });
});
const TabPanel = React.forwardRef(function TabPanel2(props, forwardedRef) {
  const { className, ...rest } = props;
  return /* @__PURE__ */ jsx(TabsPanel, { ...rest, ref: forwardedRef, className: cx("ev-tabs__panel", className) });
});
export {
  Tab,
  TabList,
  TabPanel,
  Tabs
};
//# sourceMappingURL=Tabs.js.map
